export function normalizeApiBaseUrl(rawBaseUrl: string | undefined): string {
  const fallback = "http://localhost:8000/api/v1";
  const candidate = rawBaseUrl?.trim() || fallback;

  try {
    const url = new URL(candidate);
    url.pathname = url.pathname === "/" ? "/api/v1" : url.pathname.replace(/\/$/, "");
    return url.toString().replace(/\/$/, "");
  } catch {
    return fallback;
  }
}

export function resolveApiBaseUrl(rawBaseUrl: string | undefined, hostname = globalThis.location?.hostname): string {
  const normalized = normalizeApiBaseUrl(rawBaseUrl);
  if (hostname === "crm-dev.honey.b2baisolutions.io") {
    return "https://dev.honey.b2baisolutions.io/api/v1";
  }
  if (hostname === "crm.ivankhalilov.ru") {
    return "https://honey.b2baisolutions.io/api/v1";
  }
  return normalized;
}

export const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
export const GOOGLE_ORDERS_SHEET_URL = "https://docs.google.com/spreadsheets/d/175r_oxhJR3M91OfVt1jvF8HNa-67wpTywHYKIsW3YFE/edit";
const CRM_AUTH_KEY = "crm_auth";
export const CRM_AUTH_CLEARED_EVENT = "crm-auth-cleared";

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly detail: unknown;

  constructor(message: string, status: number, url: string, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.detail = detail;
  }
}

export interface CrmAuthUser {
  id: string;
  login: string;
  email: string | null;
  role: "admin" | "manager";
  access_sections?: CrmSection[];
  can_manage_users?: boolean;
}

export interface CrmAuthState {
  access_token: string;
  token_type: string;
  user: CrmAuthUser;
}

export interface Category {
  id: string;
  parent_id: string | null;
  name: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
}

export interface HealthFilter {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
}

export type CrmSection = "catalog" | "orders" | "requests" | "analytics" | "users";

export interface StaffUser {
  id: string;
  login: string;
  email: string | null;
  role: "admin" | "manager";
  access_sections: CrmSection[];
  can_manage_users: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StaffUserCreatePayload {
  login: string;
  email: string | null;
  password: string;
  role: "admin" | "manager";
  access_sections: CrmSection[];
  can_manage_users: boolean;
  is_active: boolean;
}

export interface StaffUserUpdatePayload {
  email?: string | null;
  password?: string | null;
  role?: "admin" | "manager";
  access_sections?: CrmSection[];
  can_manage_users?: boolean;
  is_active?: boolean;
}

export interface CategoryPayload {
  parent_id: string | null;
  name: string;
  slug?: string | null;
  sort_order?: number | null;
  is_active?: boolean;
}

export interface CategoryUpdatePayload {
  parent_id?: string | null;
  name?: string;
  slug?: string | null;
  sort_order?: number | null;
  is_active?: boolean;
}

export interface ProductSku {
  sku_id: string;
  weight_label: string | null;
  weight_grams: number | null;
  package_label: string | null;
  description: string | null;
  old_price: string | number | null;
  current_price: string | number;
  is_active: boolean;
  is_wholesale?: boolean;
  show_in_catalog?: boolean;
  sort_order: number;
  images: ProductImage[];
  components: ProductSkuComponent[];
}

export interface ProductSkuComponent {
  id: string;
  component_type?: "gift" | "set_item";
  item_product_id: string | null;
  item_product_name: string | null;
  item_sku_id: string | null;
  item_sku_label: string | null;
  display_name: string | null;
  quantity: number;
  comment: string | null;
  sort_order: number;
}

export interface ProductImage {
  id: string;
  sku_id: string;
  public_url: string;
  sort_order: number;
}

export interface ProductFile {
  id: string;
  title: string;
  public_url: string;
  mime_type: string;
}

export interface Product {
  product_id: string;
  category_id: string | null;
  subcategory_id: string | null;
  product_type: "single" | "bundle";
  name: string;
  slug: string;
  description: string | null;
  composition: string | null;
  benefits: string | null;
  usage_recommendations: string | null;
  content_block: string | null;
  is_bestseller: boolean;
  is_wholesale: boolean;
  status: "draft" | "active" | "hidden";
  filter_category_ids: string[];
  health_filter_ids: string[];
  images: ProductImage[];
  files: ProductFile[];
  skus: ProductSku[];
  bundle_items: unknown[];
}

export interface ProductSkuPayload {
  sku_id?: string | null;
  weight_label: string | null;
  weight_grams: number | null;
  package_label: string | null;
  description: string | null;
  old_price: number | null;
  current_price: number;
  is_active: boolean;
  is_wholesale: boolean;
  show_in_catalog: boolean;
  sort_order: number;
  components: ProductSkuComponentPayload[];
}

export interface ProductSkuComponentPayload {
  component_type: "gift" | "set_item";
  item_product_id: string | null;
  item_sku_id: string | null;
  item_sku_index?: number | null;
  display_name: string | null;
  quantity: number;
  comment: string | null;
  sort_order: number;
}

export interface ProductPayload {
  category_id: string;
  subcategory_id: string | null;
  product_type: "single" | "bundle";
  name: string;
  slug?: string | null;
  description?: string | null;
  composition?: string | null;
  benefits?: string | null;
  usage_recommendations?: string | null;
  content_block?: string | null;
  is_bestseller: boolean;
  is_wholesale: boolean;
  status: "draft" | "active" | "hidden";
  filter_category_ids: string[];
  health_filter_ids: string[];
  skus: ProductSkuPayload[];
}

export interface CrmOrderRecipient {
  full_name: string;
  phone: string;
  email: string | null;
  delivery_address: string;
}

export interface CrmOrderItem {
  id: string;
  product_id: string | null;
  sku_id: string | null;
  title: string;
  sku_label: string | null;
  price: string | number;
  quantity: number;
  subtotal: string | number;
  is_bundle_component: boolean;
  components?: CrmOrderItemComponent[];
}

export interface CrmOrderItemComponent {
  id: string;
  component_type?: "gift" | "set_item";
  item_product_id: string | null;
  item_product_name: string | null;
  item_sku_id: string | null;
  sku_label: string | null;
  title: string;
  quantity: number;
  comment: string | null;
  sort_order: number;
}

export interface CrmOrder {
  id: string;
  order_number: string;
  source: "web" | "telegram" | string;
  status: "payment_pending" | "paid" | "processing" | "completed" | "cancelled" | "refunded" | string;
  total_amount: string | number;
  manager_comment: string | null;
  created_at: string;
  paid_at: string | null;
  recipient: CrmOrderRecipient;
  items: CrmOrderItem[];
}

export interface CrmOrderStatusUpdatePayload {
  status: "payment_pending" | "paid" | "processing" | "completed" | "cancelled" | "refunded";
}

export interface CrmOrderManagerCommentUpdatePayload {
  manager_comment: string | null;
}

interface FileDownload {
  blob: Blob;
  filename: string;
}

export interface CrmOrderQueryParams {
  source?: "web" | "telegram";
  status?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
}


export type CrmServiceRequestStatus = "new" | "processing" | "closed";
export type CrmServiceRequestSource = "web" | "telegram";
export type CrmServiceRequestType = "service" | "consultation";

export interface CrmServiceRequest {
  id: string;
  request_type: CrmServiceRequestType | string;
  source: CrmServiceRequestSource | string;
  page_slug: string | null;
  page_title: string;
  full_name: string;
  phone: string;
  email: string;
  request_text: string | null;
  status: CrmServiceRequestStatus | string;
  payload_json: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CrmServiceRequestStatusUpdatePayload {
  status: CrmServiceRequestStatus;
}

export interface CrmAnalyticsDay {
  date: string;
  visits: number;
  carts: number;
  orders: number;
}

export type CrmAnalyticsSource = "web" | "telegram";

export interface CrmAnalyticsTotals {
  visits: number;
  carts: number;
  orders: number;
}

export interface CrmAnalyticsResponse {
  date_from: string;
  date_to: string;
  days: CrmAnalyticsDay[];
  totals: CrmAnalyticsTotals;
}

function getAuth(): CrmAuthState | null {
  const raw = localStorage.getItem(CRM_AUTH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CrmAuthState;
  } catch {
    return null;
  }
}

export function getStoredAuth(): CrmAuthState | null {
  return getAuth();
}

export function setStoredAuth(auth: CrmAuthState) {
  localStorage.setItem(CRM_AUTH_KEY, JSON.stringify(auth));
}

export function clearStoredAuth() {
  localStorage.removeItem(CRM_AUTH_KEY);
  globalThis.dispatchEvent?.(new Event(CRM_AUTH_CLEARED_EVENT));
}

async function request<T>(path: string, options: Parameters<typeof fetch>[1] = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const auth = getAuth();
  if (auth?.access_token) {
    headers.set("Authorization", `Bearer ${auth.access_token}`);
  }
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });
  if (response.status === 204) return undefined as T;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/crm/auth/login")) {
      clearStoredAuth();
    }
    const detail = data?.detail || response.statusText || "Ошибка запроса к API";
    const usersEndpointHint =
      response.status === 404 && path.startsWith("/crm/users")
        ? " API управления пользователями не найден на выбранном backend."
        : "";
    throw new ApiError(`API ${response.status}: ${detail}.${usersEndpointHint} URL: ${url}`, response.status, url, data);
  }
  return data as T;
}

function crmOrderQuery(params: CrmOrderQueryParams = {}) {
  const search = new URLSearchParams();
  if (params.source) search.set("source", params.source);
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.date_from) search.set("date_from", params.date_from);
  if (params.date_to) search.set("date_to", params.date_to);
  return search.toString();
}

function filenameFromDisposition(disposition: string | null, fallback: string) {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

async function requestFile(path: string): Promise<FileDownload> {
  const headers = new Headers();
  const auth = getAuth();
  if (auth?.access_token) {
    headers.set("Authorization", `Bearer ${auth.access_token}`);
  }

  const url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/crm/auth/login")) {
      clearStoredAuth();
    }
    const data = await response.json().catch(() => null);
    const detail = data?.detail || response.statusText || "Ошибка запроса к API";
    throw new ApiError(`API ${response.status}: ${detail}. URL: ${url}`, response.status, url, data);
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get("Content-Disposition"), "orders-export.xlsx"),
  };
}

export const api = {
  login(login: string, password: string) {
    return request<CrmAuthState>("/crm/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    });
  },
  users() {
    return request<StaffUser[]>("/crm/users");
  },
  createUser(payload: StaffUserCreatePayload) {
    return request<StaffUser>("/crm/users", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateUser(userId: string, payload: StaffUserUpdatePayload) {
    return request<StaffUser>(`/crm/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  categories() {
    return request<Category[]>("/crm/categories");
  },
  async healthFilters() {
    try {
      return await request<HealthFilter[]>("/crm/health-filters");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return [];
      }
      throw error;
    }
  },
  createCategory(payload: CategoryPayload) {
    return request<Category>("/crm/categories", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateCategory(categoryId: string, payload: CategoryUpdatePayload) {
    return request<Category>(`/crm/categories/${categoryId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  products() {
    return request<Product[]>("/crm/products");
  },
  product(productId: string) {
    return request<Product>(`/crm/products/${productId}`);
  },
  createProduct(payload: ProductPayload) {
    return request<Product>("/crm/products", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateProduct(productId: string, payload: ProductPayload) {
    return request<Product>(`/crm/products/${productId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  uploadProductImage(productId: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return request<ProductImage>(`/crm/products/${productId}/images`, {
      method: "POST",
      body: formData,
    });
  },
  uploadProductImages(productId: string, files: File[]) {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    return request<ProductImage[]>(`/crm/products/${productId}/images/batch`, {
      method: "POST",
      body: formData,
    });
  },
  uploadProductSkuImages(productId: string, skuId: string, files: File[]) {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    return request<ProductImage[]>(`/crm/products/${productId}/skus/${skuId}/images/batch`, {
      method: "POST",
      body: formData,
    });
  },
  deleteProductImage(productId: string, imageId: string) {
    return request<void>(`/crm/products/${productId}/images/${imageId}`, {
      method: "DELETE",
    });
  },
  deleteProductSkuImage(productId: string, skuId: string, imageId: string) {
    return request<void>(`/crm/products/${productId}/skus/${skuId}/images/${imageId}`, {
      method: "DELETE",
    });
  },
  uploadProductFile(productId: string, file: File, title?: string) {
    const formData = new FormData();
    formData.append("file", file);
    if (title) formData.append("title", title);
    return request<ProductFile>(`/crm/products/${productId}/files`, {
      method: "POST",
      body: formData,
    });
  },
  orders(params: CrmOrderQueryParams = {}) {
    const query = crmOrderQuery(params);
    return request<CrmOrder[]>(`/crm/orders${query ? `?${query}` : ""}`);
  },
  ordersGoogleSheetUrl() {
    return GOOGLE_ORDERS_SHEET_URL;
  },
  order(orderId: string) {
    return request<CrmOrder>(`/crm/orders/${orderId}`);
  },
  updateOrderStatus(orderId: string, payload: CrmOrderStatusUpdatePayload) {
    return request<CrmOrder>(`/crm/orders/${orderId}/status`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  updateOrderManagerComment(orderId: string, payload: CrmOrderManagerCommentUpdatePayload) {
    return request<CrmOrder>(`/crm/orders/${orderId}/manager-comment`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  serviceRequests(
    params: { source?: CrmServiceRequestSource; status?: CrmServiceRequestStatus; request_type?: CrmServiceRequestType; q?: string } = {}
  ) {
    const search = new URLSearchParams();
    if (params.source) search.set("source", params.source);
    if (params.status) search.set("status", params.status);
    if (params.request_type) search.set("request_type", params.request_type);
    if (params.q) search.set("q", params.q);
    const query = search.toString();
    return request<CrmServiceRequest[]>(`/crm/service-requests${query ? `?${query}` : ""}`);
  },
  serviceRequest(requestId: string) {
    return request<CrmServiceRequest>(`/crm/service-requests/${requestId}`);
  },
  updateServiceRequestStatus(requestId: string, payload: CrmServiceRequestStatusUpdatePayload) {
    return request<CrmServiceRequest>(`/crm/service-requests/${requestId}/status`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  analytics(params: { date_from?: string; date_to?: string; source?: CrmAnalyticsSource } = {}) {
    const search = new URLSearchParams();
    if (params.date_from) search.set("date_from", params.date_from);
    if (params.date_to) search.set("date_to", params.date_to);
    if (params.source) search.set("source", params.source);
    const query = search.toString();
    return request<CrmAnalyticsResponse>(`/crm/analytics${query ? `?${query}` : ""}`);
  },
};
